import React, { useState, useEffect, useMemo } from 'react';
import { supabase } from '../../supabase';
import AdminDashboardNav from '../comps/AdminDashboardNav';
import {
  FiBarChart2, FiRefreshCw, FiTrendingUp, FiShoppingCart,
  FiDollarSign, FiCalendar, FiActivity, FiRotateCcw,
} from 'react-icons/fi';
import {
  ResponsiveContainer,
  BarChart, Bar,
  LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip,
  Cell,
} from 'recharts';
import '../styles/admin.css';

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Des'];
const WEEKDAY_LABELS = ['Man', 'Tir', 'Ons', 'Tor', 'Fre', 'Lør', 'Søn'];

const KEYWORD_CATEGORY_MAP = [
  { keywords: ['golf', 'minigolf', 'mini golf'], category: 'golf' },
  { keywords: ['lek', 'hopp', 'trampoline', 'ninja', 'klatring'], category: 'lek' },
  { keywords: ['klippekort', 'klippe', 'stempel'], category: 'klippekort' },
  { keywords: ['månedskort', 'måneds', 'månedlig', 'månedspas'], category: 'månedskort' },
  { keywords: ['gavekort', 'gave'], category: 'gavekort' },
];

function getCat(item, productCategoryMap) {
  if (item.category && item.category.toLowerCase() !== 'ukjent') return item.category.toLowerCase().trim();
  if (!item.name) return 'ukjent';
  const key = item.name.toLowerCase().trim();
  if (productCategoryMap[key] && productCategoryMap[key].toLowerCase() !== 'ukjent') return productCategoryMap[key].toLowerCase();
  for (const [pName, cat] of Object.entries(productCategoryMap)) {
    if (cat && cat.toLowerCase() !== 'ukjent' && (key.includes(pName) || pName.includes(key))) return cat.toLowerCase();
  }
  for (const { keywords, category } of KEYWORD_CATEGORY_MAP) {
    if (keywords.some(kw => key.includes(kw))) return category;
  }
  return item.name;
}

const fmtKr = v => `${Math.round(v).toLocaleString('no-NO')} kr`;
const fmtInt = v => Math.round(v).toLocaleString('no-NO');

const BAR_COLOR = '#4d6bba';
const CAT_COLORS = ['#010f3c', '#2d4499', '#4d6bba', '#7b97d4', '#adbde8', '#c7d2ef'];

function KrTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '8px 14px', boxShadow: '0 4px 12px rgba(0,0,0,0.08)', fontSize: '0.82rem' }}>
      <p style={{ margin: 0, fontWeight: '600', color: '#010f3c' }}>{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ margin: '2px 0 0', color: p.color || '#010f3c' }}>
          {p.name}: <strong>{fmtKr(p.value)}</strong>
        </p>
      ))}
    </div>
  );
}

function CountTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '8px 14px', boxShadow: '0 4px 12px rgba(0,0,0,0.08)', fontSize: '0.82rem' }}>
      <p style={{ margin: 0, fontWeight: '600', color: '#010f3c' }}>{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ margin: '2px 0 0', color: p.color || '#010f3c' }}>
          {p.name}: <strong>{p.value}</strong>
        </p>
      ))}
    </div>
  );
}

function CardShell({ children, style }) {
  return (
    <div className="card" style={{ padding: '1.75rem 2rem', ...style }}>
      {children}
    </div>
  );
}

function CardTitle({ icon: Icon, title, right }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '8px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <Icon style={{ fontSize: '1.2rem', color: '#010f3c' }} />
        <h2 style={{ margin: 0, fontSize: '1.1rem', color: '#010f3c' }}>{title}</h2>
      </div>
      {right}
    </div>
  );
}

export default function StatistikkPage() {
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statsRange, setStatsRange] = useState('year');
  const [statsCustomFrom, setStatsCustomFrom] = useState('');
  const [statsCustomTo, setStatsCustomTo] = useState('');
  // Derived from each order's own Firestore fields (refundedItems/fullyRefunded,
  // written by the backend's /refund-payment route), not localStorage - a
  // per-browser cache here would silently skew every revenue figure on this page
  // on any other browser or device.
  const refundedOrders = useMemo(() => {
    const map = {};
    orders.forEach(o => { if (o.fullyRefunded) map[o.orderReference] = true; });
    return map;
  }, [orders]);
  const refundedItems = useMemo(() => {
    const map = {};
    orders.forEach(o => { if (o.refundedItems) Object.assign(map, o.refundedItems); });
    return map;
  }, [orders]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [{ data: orderRows, error: ordersError }, { data: productRows, error: productsError }] = await Promise.all([
        supabase.from('orders')
          .select('*, order_items(*), tickets(*), klippekort(*)')
          .order('date_purchased', { ascending: false }),
        supabase.from('products').select('*'),
      ]);
      if (ordersError) throw ordersError;
      if (productsError) throw productsError;

      const allOrders = (orderRows || [])
        .filter(o => o.order_reference)
        .map(o => {
          // Ticket/klippekort rows hold the real per-unit prices, not order_items
          const subcollectionItems = [
            ...(o.tickets || []).map(t => ({ name: t.name, category: t.category, price: t.price, quantity: t.quantity })),
            ...(o.klippekort || []).map(k => ({ name: k.name, category: 'klippekort', price: null, quantity: 1 })),
          ];

          const subcollectionCategories = new Set(
            subcollectionItems.map(i => (i.category || '').toLowerCase().trim())
          );
          const baseItems = (subcollectionItems.length > 0
            ? (o.order_items || []).filter(i => {
                const cat = (i.category || '').toLowerCase().trim();
                return !subcollectionCategories.has(cat) && cat !== 'lek' && cat !== 'klippekort' && cat !== 'månedskort' && cat !== 'gavekort';
              })
            : (o.order_items || [])
          ).map(i => ({ name: i.name, category: i.category, price: i.price, quantity: i.quantity }));
          const allItems = [...baseItems, ...subcollectionItems];

          return {
            id: o.id,
            userId: o.user_id,
            orderReference: o.order_reference,
            totalPrice: o.total_price,
            captureStatus: o.capture_status,
            datePurchased: o.date_purchased,
            refundedItems: o.refunded_items,
            fullyRefunded: o.fully_refunded,
            items: allItems,
          };
        });
      setOrders(allOrders);
      setProducts(productRows || []);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, []);

  const productCategoryMap = useMemo(() => {
    const map = {};
    products.forEach(p => {
      if (p.name) map[p.name.toLowerCase()] = p.category || 'Ukjent';
      if (p.id) map[p.id.toLowerCase()] = p.category || 'Ukjent';
    });
    return map;
  }, [products]);

  const filteredOrders = useMemo(() => {
    const now = new Date();
    let bounds = null;
    switch (statsRange) {
      case 'today': { const s = new Date(now); s.setHours(0,0,0,0); const e = new Date(now); e.setHours(23,59,59,999); bounds = { from: s, to: e }; break; }
      case 'week': { const s = new Date(now); s.setDate(now.getDate()-6); s.setHours(0,0,0,0); const e = new Date(now); e.setHours(23,59,59,999); bounds = { from: s, to: e }; break; }
      case 'month': { bounds = { from: new Date(now.getFullYear(), now.getMonth(), 1), to: new Date(now.getFullYear(), now.getMonth()+1, 0, 23,59,59,999) }; break; }
      case 'lastmonth': { bounds = { from: new Date(now.getFullYear(), now.getMonth()-1, 1), to: new Date(now.getFullYear(), now.getMonth(), 0, 23,59,59,999) }; break; }
      case 'year': { bounds = { from: new Date(now.getFullYear(), 0, 1), to: new Date(now.getFullYear(), 11, 31, 23,59,59,999) }; break; }
      case 'custom': { bounds = statsCustomFrom && statsCustomTo ? { from: new Date(statsCustomFrom), to: new Date(new Date(statsCustomTo).setHours(23,59,59,999)) } : null; break; }
      default: bounds = null;
    }
    // Exclude pending/cancelled orders (captureStatus not CAPTURED and not refunded)
    const active = orders.filter(o =>
      o.captureStatus === 'CAPTURED' || !!refundedOrders[o.orderReference]
    );
    if (!bounds) return active;
    return active.filter(o => { const d = new Date(o.datePurchased || 0); return d >= bounds.from && d <= bounds.to; });
  }, [orders, statsRange, statsCustomFrom, statsCustomTo, refundedOrders]);

  // Monthly revenue (12 months)
  const monthlyData = useMemo(() => {
    const buckets = Array.from({ length: 12 }, (_, i) => ({ month: i, label: MONTH_LABELS[i], revenue: 0, orders: 0 }));
    filteredOrders.forEach(o => {
      const m = new Date(o.datePurchased || 0).getMonth();
      buckets[m].revenue += o.totalPrice || 0;
      buckets[m].orders += 1;
    });
    return buckets;
  }, [filteredOrders]);

  // Weekday distribution
  const weekdayData = useMemo(() => {
    const buckets = Array(7).fill(0);
    filteredOrders.forEach(o => {
      const d = new Date(o.datePurchased || 0);
      // JS: 0=Sun,1=Mon,...6=Sat â†’ remap to Mon=0..Sun=6
      const wd = (d.getDay() + 6) % 7;
      buckets[wd] += o.totalPrice || 0;
    });
    return WEEKDAY_LABELS.map((label, i) => ({ label, revenue: buckets[i] }));
  }, [filteredOrders]);

  // Category revenue — uses o.totalPrice as authoritative amount, distributed by item-price ratio
  const categoryData = useMemo(() => {
    const map = {};
    filteredOrders.forEach(o => {
      const orderTotal = o.totalPrice || 0;
      if (orderTotal <= 0) return;
      const items = o.items || o.products || [];
      if (items.length > 0) {
        // Compute item weights (fall back to equal share if all prices are missing/0)
        const weights = items.map(item => (item.price || 0) * (item.quantity || 1));
        const weightSum = weights.reduce((s, w) => s + w, 0);
        items.forEach((item, idx) => {
          const cat = getCat(item, productCategoryMap);
          // If items have real prices, distribute proportionally; otherwise split equally
          const share = weightSum > 0 ? (weights[idx] / weightSum) * orderTotal : orderTotal / items.length;
          if (!map[cat]) map[cat] = { value: 0, count: 0 };
          map[cat].value += share;
          map[cat].count += item.quantity || 1;
        });
      } else {
        // Try to find category from order-level name fields
        const orderKey = (o.productName || o.orderName || o.name || '').toLowerCase().trim();
        if (orderKey) {
          const catFromMap = productCategoryMap[orderKey];
          let cat = null;
          if (catFromMap && catFromMap.toLowerCase() !== 'ukjent') {
            cat = catFromMap;
          } else {
            const matched = KEYWORD_CATEGORY_MAP.find(({ keywords }) => keywords.some(kw => orderKey.includes(kw)));
            if (matched) cat = matched.category;
          }
          if (cat) {
            if (!map[cat]) map[cat] = { value: 0, count: 0 };
            map[cat].value += orderTotal;
            map[cat].count += 1;
          }
        }
      }
    });
    return Object.entries(map)
      .map(([label, { value, count }]) => ({ label: label.charAt(0).toUpperCase() + label.slice(1), value, count }))
      .sort((a, b) => b.value - a.value);
  }, [filteredOrders, productCategoryMap]);

  const totalRevenue = filteredOrders.reduce((s, o) => s + (o.totalPrice || 0), 0);
  const totalRefunds = filteredOrders.reduce((s, o) => {
    if (refundedOrders && refundedOrders[o.orderReference]) return s + (o.totalPrice || 0);
    const items = o.items || o.products || [];
    return s + items.reduce((is, item) => {
      const qty = (refundedItems && refundedItems[`${o.orderReference}_${item.name}`]) || 0;
      return is + qty * (item.price || 0);
    }, 0);
  }, 0);
  const netRevenue = totalRevenue - totalRefunds;
  const categoryTotal = useMemo(() => categoryData.reduce((s, r) => s + r.value, 0), [categoryData]);
  const categoryTotalCount = useMemo(() => categoryData.reduce((s, r) => s + r.count, 0), [categoryData]);
  const totalOrderCount = filteredOrders.length;
  const avgOrderValue = totalOrderCount > 0 ? totalRevenue / totalOrderCount : 0;
  const bestMonth = monthlyData.reduce((best, m) => m.revenue > best.revenue ? m : best, { revenue: -1, label: '—' });

  const skeletonBars = (h = 220, n = 12) => (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: '6px', height: `${h}px` }}>
      {Array.from({ length: n }).map((_, i) => (
        <div key={i} className="skeleton" style={{ flex: 1, height: `${30 + (i * 17 % 55)}%`, borderRadius: '5px 5px 0 0' }} />
      ))}
    </div>
  );

  return (
    <>
      <AdminDashboardNav />
      <div className="admin-dashboard">

        <div className="admin-header">
          <div>
            <h1><FiBarChart2 style={{ marginRight: '12px', verticalAlign: 'middle' }} />Statistikk</h1>
            <p>Visuell oversikt over inntekter, bestillinger og kategorier</p>
          </div>
          <div className="admin-actions">
            <button className="refresh-btn" onClick={fetchData} disabled={loading}>
              <FiRefreshCw />
              {loading ? 'Laster...' : 'Oppdater data'}
            </button>
          </div>
        </div>

        {/* Period filter */}
        {(() => {
          const periodOpts = [
            { value: 'today', label: 'I dag' },
            { value: 'week', label: 'Siste 7 dager' },
            { value: 'month', label: 'Denne måneden' },
            { value: 'lastmonth', label: 'Forrige måned' },
            { value: 'year', label: 'I år' },
            { value: 'all', label: 'Alle tider' },
            { value: 'custom', label: 'Egendefinert' },
          ];
          const customDates = statsRange === 'custom' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
              <div style={{ position: 'relative', display: 'inline-block' }}>
                <input type="date" value={statsCustomFrom} onChange={e => setStatsCustomFrom(e.target.value)}
                  style={{ height: '34px', borderRadius: '8px', border: '1px solid #dde1ea', padding: '0 10px', fontSize: '0.82rem', color: 'transparent', minWidth: '120px' }} />
                <span style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', fontSize: '0.82rem', color: '#1e293b', pointerEvents: 'none' }}>
                  {statsCustomFrom ? statsCustomFrom.split('-').reverse().join('.') : 'Fra'}
                </span>
              </div>
              <div style={{ position: 'relative', display: 'inline-block' }}>
                <input type="date" value={statsCustomTo} onChange={e => setStatsCustomTo(e.target.value)}
                  style={{ height: '34px', borderRadius: '8px', border: '1px solid #dde1ea', padding: '0 10px', fontSize: '0.82rem', color: 'transparent', minWidth: '120px' }} />
                <span style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', fontSize: '0.82rem', color: '#1e293b', pointerEvents: 'none' }}>
                  {statsCustomTo ? statsCustomTo.split('-').reverse().join('.') : 'Til'}
                </span>
              </div>
            </div>
          );
          return (
            <div className="report-filter-bar">
              <span style={{ fontSize: '0.75rem', fontWeight: '600', color: '#64748b', letterSpacing: '0.04em', textTransform: 'uppercase', flexShrink: 0 }}>Oversikt:</span>
              <div className="report-filter-tiles">
                {periodOpts.map(opt => (
                  <button
                    key={opt.value}
                    onClick={() => setStatsRange(opt.value)}
                    style={{
                      padding: '0.3rem 0.85rem', borderRadius: '7px',
                      border: statsRange === opt.value ? '2px solid #010f3c' : '2px solid #dde1ea',
                      background: statsRange === opt.value ? '#010f3c' : '#fff',
                      color: statsRange === opt.value ? '#fff' : '#475569',
                      fontWeight: '500', fontSize: '0.8rem', cursor: 'pointer',
                      fontFamily: 'Poppins, sans-serif',
                      transition: 'background 0.15s, border-color 0.15s, color 0.15s',
                    }}
                  >{opt.label}</button>
                ))}
                {customDates}
              </div>
              <div className="report-filter-dropdown-wrapper">
                <select
                  className="report-filter-select"
                  value={statsRange}
                  onChange={e => setStatsRange(e.target.value)}
                >
                  {periodOpts.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                </select>
                {customDates}
              </div>
            </div>
          );
        })()}

        <div className="stats-grid" style={{ marginTop: '1.5rem', gridTemplateColumns: 'repeat(3, 1fr)' }}>
          <div className="stat-card primary">
            <div className="stat-card-header">
              <div className="stat-icon-circle"><FiDollarSign /></div>
              <span className="stat-label">Brutto omsetning</span>
            </div>
            <div className="stat-value">{loading ? <span className="skeleton skeleton-value" /> : fmtKr(totalRevenue)}</div>
          </div>
          <div className="stat-card">
            <div className="stat-card-header">
              <div className="stat-icon-circle"><FiActivity /></div>
              <span className="stat-label">Netto omsetning</span>
            </div>
            <div className="stat-value">{loading ? <span className="skeleton skeleton-value" /> : fmtKr(netRevenue)}</div>
            {!loading && totalRefunds > 0 && (
              <span className="stat-badge">brutto minus refusjoner</span>
            )}
          </div>
          <div className="stat-card warning">
            <div className="stat-card-header">
              <div className="stat-icon-circle"><FiRotateCcw /></div>
              <span className="stat-label">Refusjoner</span>
            </div>
            <div className="stat-value">{loading ? <span className="skeleton skeleton-value" /> : fmtKr(totalRefunds)}</div>
            {!loading && totalRefunds > 0 && (
              <span className="stat-badge">{filteredOrders.filter(o => refundedOrders[o.orderReference]).length} fulle + delvise</span>
            )}
          </div>
          <div className="stat-card secondary">
            <div className="stat-card-header">
              <div className="stat-icon-circle"><FiShoppingCart /></div>
              <span className="stat-label">Bestillinger</span>
            </div>
            <div className="stat-value">{loading ? <span className="skeleton skeleton-value" /> : fmtInt(totalOrderCount)}</div>
          </div>
          <div className="stat-card accent">
            <div className="stat-card-header">
              <div className="stat-icon-circle"><FiTrendingUp /></div>
              <span className="stat-label">Snitt per ordre</span>
            </div>
            <div className="stat-value">{loading ? <span className="skeleton skeleton-value" /> : fmtKr(avgOrderValue)}</div>
            {!loading && totalOrderCount > 0 && (
              <span className="stat-badge">basert på {fmtInt(totalOrderCount)} ordrer</span>
            )}
          </div>
          <div className="stat-card accent">
            <div className="stat-card-header">
              <div className="stat-icon-circle"><FiCalendar /></div>
              <span className="stat-label">Beste måned</span>
            </div>
            <div className="stat-value" style={{ fontSize: '1.6rem' }}>{loading ? <span className="skeleton skeleton-value" /> : bestMonth.label}</div>
            {!loading && bestMonth.revenue > 0 && (
              <span className="stat-badge">{fmtKr(bestMonth.revenue)}</span>
            )}
          </div>
        </div>
        {/* Category chart + table — full width, chart left / table right */}
        <CardShell style={{ marginTop: '1.5rem' }}>
          <CardTitle
            icon={FiTrendingUp}
            title="Inntekt per kategori"
            right={!loading && categoryData.length > 0 ? (
              <span className="stat-badge">{categoryData.length} kategorier</span>
            ) : null}
          />
          {loading ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {[1, 2, 3, 4].map(i => <span key={i} className="skeleton" style={{ height: '18px', borderRadius: '4px', display: 'block' }} />)}
            </div>
          ) : categoryData.length === 0 ? (
            <p style={{ color: '#94a3b8', fontSize: '0.9rem' }}>Ingen data for valgt periode</p>
          ) : (
            <div className="report-filter-bar" style={{ alignItems: 'start', gap: '2rem', flexWrap: 'nowrap' }}>
              <div style={{ flex: '1 1 0', minWidth: 0 }}>
                <ResponsiveContainer width="100%" height={Math.max(200, categoryData.length * 48)}>
                  <BarChart
                    layout="vertical"
                    data={categoryData}
                    barCategoryGap="25%"
                    margin={{ top: 0, right: 60, left: 0, bottom: 0 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
                    <XAxis type="number" tickFormatter={v => `${(v / 1000).toFixed(0)}k`} tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                    <YAxis type="category" dataKey="label" tick={{ fontSize: 12, fill: '#475569', fontWeight: 500 }} axisLine={false} tickLine={false} width={90} />
                    <Tooltip content={<KrTooltip />} cursor={{ fill: '#f0f4ff' }} />
                    <Bar dataKey="value" name="Omsetning" radius={[0, 4, 4, 0]}>
                      {categoryData.map((_, i) => (
                        <Cell key={i} fill={CAT_COLORS[i % CAT_COLORS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div style={{ flex: '1 1 0', minWidth: 0 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.83rem' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid #e8ecf2' }}>
                      <th style={{ textAlign: 'left', padding: '6px 8px 8px', color: '#64748b', fontWeight: '600', fontSize: '0.7rem', letterSpacing: '0.04em', textTransform: 'uppercase' }}>Kategori</th>
                      <th style={{ textAlign: 'right', padding: '6px 8px 8px', color: '#64748b', fontWeight: '600', fontSize: '0.7rem', letterSpacing: '0.04em', textTransform: 'uppercase' }}>Inntekt</th>
                      <th style={{ textAlign: 'right', padding: '6px 8px 8px', color: '#64748b', fontWeight: '600', fontSize: '0.7rem', letterSpacing: '0.04em', textTransform: 'uppercase' }}>Antall solgt</th>
                      <th style={{ textAlign: 'right', padding: '6px 8px 8px', color: '#64748b', fontWeight: '600', fontSize: '0.7rem', letterSpacing: '0.04em', textTransform: 'uppercase' }}>Andel</th>
                      <th style={{ textAlign: 'right', padding: '6px 8px 8px', color: '#64748b', fontWeight: '600', fontSize: '0.7rem', letterSpacing: '0.04em', textTransform: 'uppercase' }}>Eks. MVA</th>
                    </tr>
                  </thead>
                  <tbody>
                    {categoryData.map((row, i) => {
                      const pct = categoryTotal > 0 ? (row.value / categoryTotal) * 100 : 0;
                      return (
                        <tr key={i} style={{ borderBottom: '1px solid #f1f5f9', background: i % 2 === 0 ? '#fff' : '#f8fafc' }}>
                          <td style={{ padding: '8px 8px', color: '#1e293b' }}>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '7px' }}>
                              <span style={{ width: '10px', height: '10px', borderRadius: '3px', background: CAT_COLORS[i % CAT_COLORS.length], display: 'inline-block', flexShrink: 0 }} />
                              {row.label}
                            </span>
                          </td>
                          <td style={{ padding: '8px 8px', textAlign: 'right', color: '#1e293b', fontWeight: '500' }}>{fmtKr(row.value)}</td>
                          <td style={{ padding: '8px 8px', textAlign: 'right', color: '#1e293b' }}>{row.count}</td>
                          <td style={{ padding: '8px 8px', textAlign: 'right' }}>
                            <span style={{ background: '#f0f4ff', color: '#4d6bba', borderRadius: '5px', padding: '2px 7px', fontSize: '0.72rem', fontWeight: '600' }}>
                              {pct.toFixed(1)}%
                            </span>
                          </td>
                          <td style={{ padding: '8px 8px', textAlign: 'right', color: '#64748b' }}>{fmtKr(row.value / 1.12)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr style={{ borderTop: '2px solid #e8ecf2', background: '#f0f4ff' }}>
                      <td style={{ padding: '8px 8px', fontWeight: '700', color: '#010f3c' }}>Totalt</td>
                      <td style={{ padding: '8px 8px', textAlign: 'right', fontWeight: '700', color: '#010f3c' }}>{fmtKr(categoryTotal)}</td>
                      <td style={{ padding: '8px 8px', textAlign: 'right', fontWeight: '700', color: '#010f3c' }}>{categoryTotalCount}</td>
                      <td style={{ padding: '8px 8px', textAlign: 'right' }}>
                        <span style={{ background: '#e8ecf8', color: '#010f3c', borderRadius: '5px', padding: '2px 7px', fontSize: '0.72rem', fontWeight: '700' }}>100%</span>
                      </td>
                      <td style={{ padding: '8px 8px', textAlign: 'right', fontWeight: '700', color: '#010f3c' }}>{fmtKr(categoryTotal / 1.12)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          )}
        </CardShell>
        {/* Trend line – full width */}
        <CardShell style={{ marginTop: '1.5rem' }}>
          <CardTitle
            icon={FiTrendingUp}
            title="Omsetningstrend"
            right={<span style={{ fontSize: '0.78rem', color: '#94a3b8' }}>NOK per måned</span>}
          />
          {loading ? skeletonBars(200) : (
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={monthlyData} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                <YAxis tickFormatter={v => `${(v / 1000).toFixed(0)}k`} tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} width={40} />
                <Tooltip content={<KrTooltip />} />
                <Line
                  type="monotone"
                  dataKey="revenue"
                  name="Omsetning"
                  stroke="#010f3c"
                  strokeWidth={2.5}
                  dot={{ r: 4, fill: '#010f3c', stroke: '#fff', strokeWidth: 2 }}
                  activeDot={{ r: 6, fill: '#010f3c' }}
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </CardShell>
        {/* Monthly revenue – full width */}
        <CardShell style={{ marginTop: '1.5rem' }}>
          <CardTitle
            icon={FiBarChart2}
            title="Månedlig omsetning"
            right={<span style={{ fontSize: '0.78rem', color: '#94a3b8' }}>NOK</span>}
          />
          {loading ? skeletonBars(220) : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={monthlyData} barCategoryGap="30%" margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                <YAxis tickFormatter={v => `${(v / 1000).toFixed(0)}k`} tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} width={40} />
                <Tooltip content={<KrTooltip />} cursor={{ fill: '#f0f4ff' }} />
                <Bar dataKey="revenue" name="Omsetning" fill={BAR_COLOR} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </CardShell>

        {/* Two-column: weekday + orders per month */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>

          <CardShell>
            <CardTitle icon={FiActivity} title="Omsetning per ukedag" />
            {loading ? skeletonBars(200, 7) : (
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={weekdayData} barCategoryGap="30%" margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                  <YAxis tickFormatter={v => `${(v / 1000).toFixed(0)}k`} tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} width={40} />
                  <Tooltip content={<KrTooltip />} cursor={{ fill: '#f0f4ff' }} />
                  <Bar dataKey="revenue" name="Omsetning" fill={BAR_COLOR} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardShell>

          <CardShell>
            <CardTitle icon={FiShoppingCart} title="Bestillinger per måned" />
            {loading ? skeletonBars(200) : (
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={monthlyData} barCategoryGap="30%" margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} width={32} />
                  <Tooltip content={<CountTooltip />} cursor={{ fill: '#f0f4ff' }} />
                  <Bar dataKey="orders" name="Bestillinger" fill={BAR_COLOR} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardShell>

        </div>

        {/* Monthly table — full width */}
        <CardShell>
            <CardTitle icon={FiCalendar} title="Månedlig oversikt" />
            {loading ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {[1, 2, 3].map(i => <span key={i} className="skeleton" style={{ height: '36px', borderRadius: '8px', display: 'block' }} />)}
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.84rem' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid #e8ecf2' }}>
                      <th style={{ textAlign: 'left', padding: '7px 8px 9px', color: '#64748b', fontWeight: '600', fontSize: '0.72rem', letterSpacing: '0.04em', textTransform: 'uppercase' }}>Måned</th>
                      <th style={{ textAlign: 'right', padding: '7px 8px 9px', color: '#64748b', fontWeight: '600', fontSize: '0.72rem', letterSpacing: '0.04em', textTransform: 'uppercase' }}>Omsetn.</th>
                      <th style={{ textAlign: 'right', padding: '7px 8px 9px', color: '#64748b', fontWeight: '600', fontSize: '0.72rem', letterSpacing: '0.04em', textTransform: 'uppercase' }}>Ordr.</th>
                      <th style={{ textAlign: 'right', padding: '7px 8px 9px', color: '#64748b', fontWeight: '600', fontSize: '0.72rem', letterSpacing: '0.04em', textTransform: 'uppercase' }}>Eks. MVA</th>
                    </tr>
                  </thead>
                  <tbody>
                    {monthlyData.map((row, i) => (
                      <tr key={i} style={{
                        borderBottom: '1px solid #f1f5f9',
                        background: i % 2 === 0 ? '#fff' : '#f8fafc',
                        fontWeight: '400',
                      }}>
                        <td style={{ padding: '8px 8px', color: '#1e293b' }}>{MONTH_LABELS[i]}</td>
                        <td style={{ padding: '8px 8px', textAlign: 'right', color: '#1e293b' }}>{row.revenue > 0 ? fmtKr(row.revenue) : <span style={{ color: '#c7cdd8' }}>—</span>}</td>
                        <td style={{ padding: '8px 8px', textAlign: 'right', color: '#475569' }}>{row.orders > 0 ? row.orders : <span style={{ color: '#c7cdd8' }}>—</span>}</td>
                        <td style={{ padding: '8px 8px', textAlign: 'right', color: '#1e293b' }}>{row.revenue > 0 ? fmtKr(row.revenue / 1.12) : <span style={{ color: '#c7cdd8' }}>—</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr style={{ borderTop: '2px solid #e8ecf2', background: '#f0f4ff' }}>
                      <td style={{ padding: '8px 8px', fontWeight: '700', color: '#010f3c' }}>Totalt</td>
                      <td style={{ padding: '8px 8px', textAlign: 'right', fontWeight: '700', color: '#010f3c' }}>{fmtKr(totalRevenue)}</td>
                      <td style={{ padding: '8px 8px', textAlign: 'right', fontWeight: '700', color: '#010f3c' }}>{fmtInt(totalOrderCount)}</td>
                      <td style={{ padding: '8px 8px', textAlign: 'right', fontWeight: '700', color: '#010f3c' }}>{fmtKr(totalRevenue / 1.12)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </CardShell>

        </div>
    </>
  );
}

